import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { tap } from 'rxjs';
import { TranslatePipe, TranslateService, TranslateModule } from '@ngx-translate/core';

import { ListingCategories } from '@listings/enums/listing-categories.enum';
import { ListingCategoryTexts } from '@listings/enums/listing-category-texts.enum';
import { ListingSearchOptions } from '@listings/models/search/listing-search-options';
import { SEARCH_FILTER_INITIAL_VALUES, createBedsValues } from '@search/constants/search-filter-initial-values.constants';
import { SearchFilterTitlesService } from '@search/services/search-filter-titles.service';
import { SearchStoreService } from '@search/store/services/search-store.service';
import { SearchRunMode } from '@search/enums/search-run-mode.enum';
import { RouteService } from '@core-layout/app/services/route.service';
import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { CheckboxFilterValue } from '@search/components/checkbox-filter/models/checkbox-filter-value';
import { CheckboxFilterOption } from '@listings/models/search/checkbox-filter-option';
import { SimpleChanges } from '@core-models/utilities/generic-simple-changes';
import { RpcButtonToggleItem } from '@core-controls/components/rpc-button-toggle/models/rpc-button-toggle-item';
import { ListingSelectionService } from '@listings/services/listing-selection.service';
import { RpcButtonToggleComponent } from '../../../core-modules/controls/components/rpc-button-toggle/rpc-button-toggle.component';
import { OmniSearchComponent } from '../../../search/components/omni-search/omni-search.component';
import { RpcButtonComponent } from '../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { RpcDropdownDirective } from '../../../core-modules/directives/rpc-dropdown/rpc-dropdown.directive';
import { RangeFilterComponent } from '../../../search/components/range-filter/range-filter.component';
import { CheckboxFilterComponent } from '../../../search/components/checkbox-filter/checkbox-filter.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'landing-search',
    templateUrl: './landing-search.component.html',
    styleUrls: ['./landing-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RpcButtonToggleComponent, OmniSearchComponent, RpcButtonComponent, RpcDropdownDirective, RangeFilterComponent, CheckboxFilterComponent, AsyncPipe, TranslateModule]
})
export class LandingSearchComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public isSticky?: boolean = null;

    public readonly listingCategory = ListingCategoryTexts;
    public readonly listingCategoryIds = ListingCategories;
    public readonly searchFilterTitlesService: SearchFilterTitlesService;
    public readonly searchFilterInitialValues = SEARCH_FILTER_INITIAL_VALUES;

    public priceTitle: string;
    public bedsTitle: string;
    public bedsValues: CheckboxFilterValue[];

    public readonly searchOptions$ = this.searchStoreService.searchOptions$.pipe(
        tap((searchOptions) => {
            this.priceTitle = this.searchFilterTitlesService.createRangeFilterTitle(
                'LANDING_SEARCH.TITLES.PRICE',
                searchOptions?.priceSearchOption,
                'LANDING_SEARCH.FILTERS.UP_TO'
            );

            this.bedsTitle = this.searchFilterTitlesService.createCheckboxFilterTitle(
                createBedsValues('LANDING_SEARCH.FILTERS.BED.STUDIO'),
                'LANDING_SEARCH.TITLES.BEDS',
                searchOptions?.bedSearchOption?.values,
                searchOptions?.bedSearchOption?.exactMatch
            );
        })
    );

    public readonly categoryItems: RpcButtonToggleItem[] = [
        { value: ListingCategoryTexts.Sales, text: 'LANDING_SEARCH.TITLES.BUY', onClick: () => this.onListingCategoryChanged(ListingCategories.Sales) },
        { value: ListingCategoryTexts.Rental, text: 'LANDING_SEARCH.TITLES.RENT', onClick: () => this.onListingCategoryChanged(ListingCategories.Rental) }
    ];

    private readonly translatePipe = new TranslatePipe(this.translateService, this.changeDetectorRef);

    constructor(
        public readonly searchStoreService: SearchStoreService,
        private readonly renderer: Renderer2,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly translateService: TranslateService,
        private readonly routeService: RouteService,
        private readonly listingSelectionService: ListingSelectionService
    ) {
        this.searchFilterTitlesService = new SearchFilterTitlesService(this.translatePipe);
    }

    public ngOnInit(): void {
        this.searchStoreService.loadNeighborhoodProperties();
        this.searchStoreService.clearSearchCriteria();
        this.listingSelectionService.unselectAll();

        this.searchStoreService.setSearchRunMode(SearchRunMode.Manually);

        this.bedsValues = createBedsValues('LANDING_SEARCH.FILTERS.BED.STUDIO');
    }

    public ngOnChanges(changes: SimpleChanges<LandingSearchComponent>): void {
        if (changes.isSticky) {
            if (this.isSticky) {
                this.renderer.addClass(document.body, 'rpc-landing-page--sticky');
            } else {
                this.renderer.removeClass(document.body, 'rpc-landing-page--sticky');
            }
        }
    }

    public ngOnDestroy(): void {
        this.renderer.removeClass(document.body, 'rpc-landing-page--sticky');
    }

    public onListingCategoryChanged(listingCategory: ListingCategories): void {
        this.setSearchOptions('categoryId', listingCategory);
    }

    public onPriceChanged(value: { min: number; max: number; }): void {
        this.setSearchOptions('priceSearchOption', { min: value.min, max: value.max });
    }

    public onBedsChanged(value: CheckboxFilterOption): void {
        this.setSearchOptions('bedSearchOption', value);
    }

    public onSearch(): void {
        this.searchStoreService.search();

        this.routeService.navigate(RpcRoute.SearchListings, null, RpcRoute.FindHome).then().catch(() => { });
    }

    private setSearchOptions<Key extends keyof ListingSearchOptions>(valueFieldName: Key, value: ListingSearchOptions[Key]): void {
        this.searchStoreService.changeSearchOptions({ [valueFieldName]: value });
    }
}
